import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

/**
 * Card — Agent Builder's canonical container.
 *   bg-white border-gray-200 / dark:bg-gray-900/80 dark:border-gray-800
 *   rounded-xl overflow-hidden
 *
 * Variants:
 *   default       static container
 *   interactive   adds hover affordance for clickable cards (project rows)
 */
interface CardProps extends HTMLAttributes<HTMLDivElement> {
    isDarkMode?: boolean;
    variant?: 'default' | 'interactive';
    padding?: 'none' | 'sm' | 'md' | 'lg';
    children: ReactNode;
}

const PADDING = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
};

const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
    { isDarkMode = false, variant = 'default', padding = 'md', className, children, ...rest },
    ref,
) {
    return (
        <div
            ref={ref}
            className={cn(
                'rounded-xl border overflow-hidden',
                isDarkMode
                    ? 'bg-gray-900/80 border-gray-800'
                    : 'bg-white border-gray-200',
                variant === 'interactive' &&
                    (isDarkMode
                        ? 'hover:bg-gray-800/80 hover:border-gray-700 transition-colors duration-150 cursor-pointer'
                        : 'hover:bg-gray-50 hover:border-gray-300 transition-colors duration-150 cursor-pointer'),
                PADDING[padding],
                className,
            )}
            {...rest}
        >
            {children}
        </div>
    );
});

export default Card;
